import React from 'react';
import { Box, Button } from '@mui/material';
import { MailOutline as EmailIcon, Telegram as TelegramIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConnectTelegramDialog, { useConnectTelegramFor } from '../../contexts/ConnectTelegramDialog';
import { telegramShareUrl } from '../../../config/telegram';
import type { ContextBalances, FinancialContext } from '../../../services/contextApi';
import { tokens } from '../../../theme/tokens';
import { StepItem, StepList } from '../StepList';
import { GroupHeader } from './GroupHome';
import { groupSetupSteps, GroupSetupStep } from './groupSetup';

interface Props {
  group: FinancialContext;
  balances: ContextBalances | null;
  currentUserId?: string;
  /** Invitations to sign up still open; only owners and admins can see them. */
  signupInvitations?: number;
  /** Opens Membros, where people are invited by email. */
  onInvite: () => void;
}

const buttonSx = { minHeight: 44 } as const;

/**
 * "Monte o grupo": a group's Início until its first entry, instead of a
 * dashboard of zeros. Three steps, Telegram, people and a first expense,
 * each with its way to do it for this user; the first one they can do gets
 * the filled button. A member, who can't connect or invite, is told who
 * does and can still record an expense here; a viewer only sees where it stands.
 */
export const GroupSetup: React.FC<Props> = ({ group, balances, currentUserId, signupInvitations = 0, onInvite }) => {
  const { t } = useTranslation(['dashboard', 'contexts']);
  const navigate = useNavigate();
  const { openDialog, dialogProps } = useConnectTelegramFor(group);
  const setup = groupSetupSteps({ group, members: balances?.members ?? [], signupInvitations });
  const { chat } = setup;

  const variant = (step: GroupSetupStep) => (setup.next === step.key ? 'contained' : 'outlined');
  const addHere = (shown: 'contained' | 'outlined') => (
    <Button variant={shown} onClick={() => navigate('/transactions', { state: { newTransaction: true } })} sx={buttonSx}>
      {t('dashboard:firstSteps.steps.personalExpense.addHere')}
    </Button>
  );
  const byEmail = (shown: 'contained' | 'outlined') => (
    <Button variant={shown} startIcon={<EmailIcon />} onClick={onInvite} sx={buttonSx}>
      {t('dashboard:group.setup.steps.invite.byEmail')}
    </Button>
  );

  const item = (step: GroupSetupStep): StepItem => {
    const base = { key: step.key, title: t(`dashboard:group.setup.steps.${step.key}.title`), done: step.done, current: setup.next === step.key };
    switch (step.key) {
      case 'telegram':
        return step.canAct
          ? {
              ...base,
              hint: t('dashboard:group.setup.steps.telegram.hint'),
              actions: (
                <Button variant={variant(step)} startIcon={<TelegramIcon />} onClick={openDialog} sx={buttonSx}>
                  {t('contexts:connect.cardButton')}
                </Button>
              ),
            }
          : { ...base, hint: t('dashboard:group.setup.steps.telegram.hintMember') };
      case 'invite': {
        const hint = t('dashboard:group.setup.steps.invite.hint');
        if (!step.canAct) return { ...base, hint };
        if (chat?.inviteLink) {
          return {
            ...base,
            hint,
            actions: (
              <>
                <Button
                  variant={variant(step)}
                  startIcon={<TelegramIcon />}
                  href={telegramShareUrl(chat.inviteLink, t('contexts:connect.inviteText', { name: group.name }))}
                  target='_blank'
                  rel='noopener noreferrer'
                  sx={buttonSx}
                >
                  {t('contexts:connect.invite')}
                </Button>
                {byEmail('outlined')}
              </>
            ),
          };
        }
        return {
          ...base,
          hint,
          actions: (
            <>
              {byEmail(variant(step))}
              {/* Linked, but Fin can't make an invite link until it is an admin there. */}
              {chat && (
                <Box component='span' sx={{ flexBasis: '100%', fontSize: 13, color: tokens.ink3, lineHeight: 1.45 }}>
                  {t('contexts:connect.inviteNeedsAdmin')}
                </Box>
              )}
            </>
          ),
        };
      }
      default:
        // A viewer can't record anything, so there is nothing to offer.
        if (!step.canAct) return base;
        if (!chat) return { ...base, hint: t('dashboard:group.setup.steps.firstExpense.hintNoChat'), actions: addHere(variant(step)) };
        return {
          ...base,
          hint: t('dashboard:group.setup.steps.firstExpense.hint'),
          actions: chat.url ? (
            <>
              <Button variant={variant(step)} startIcon={<TelegramIcon />} href={chat.url} target='_blank' rel='noopener noreferrer' sx={buttonSx}>
                {t('dashboard:group.setup.steps.firstExpense.openChat')}
              </Button>
              {addHere('outlined')}
            </>
          ) : (
            addHere(variant(step))
          ),
        };
    }
  };

  // Someone who can't connect or invite is told who sets the group up while
  // that is still open, and that it is ready once it isn't. A viewer, who
  // can't record either, is never told to.
  const viewer = group.memberRole === 'viewer';
  const othersStepsOpen = setup.steps.some((step) => !step.done && step.key !== 'firstExpense');
  const intro = setup.manages
    ? 'lead'
    : othersStepsOpen
      ? viewer ? 'leadViewer' : 'leadMember'
      : viewer ? 'leadViewerReady' : 'leadMemberReady';

  return (
    <Box sx={{ display: 'grid', gap: { xs: 2, md: 2.5 } }}>
      <GroupHeader group={group} balances={balances} currentUserId={currentUserId} />
      <StepList
        title={t('dashboard:group.setup.title')}
        intro={t(`dashboard:group.setup.${intro}`)}
        steps={setup.steps.map(item)}
      />
      <ConnectTelegramDialog {...dialogProps} />
    </Box>
  );
};
